import { X } from 'lucide-react'
import type { InviteeLookupResult } from '../types/invitee'
import {
  formatDateForDisplay,
  formatPhoneNumber,
  formatSSNForDisplay,
} from '../utils/inviteeFormatters'
import modalStyles from '../styles/modals.module.css'
import chromeStyles from '@/components/ui/confirmDialogChrome.module.css'

interface InviteeLookupDialogProps {
  isOpen: boolean
  lookupResult: InviteeLookupResult | null
  onConfirm: () => void
  onCancel: () => void
  showSSN?: boolean
  showDOB?: boolean
}

export function InviteeLookupDialog({
  isOpen,
  lookupResult,
  onConfirm,
  onCancel,
  showSSN = false,
  showDOB = false,
}: InviteeLookupDialogProps) {
  if (!isOpen || !lookupResult) return null

  return (
    <div
      data-testid="invitee-lookup-dialog"
      className={`${modalStyles.modalOverlay} !z-[9999]`}
      onClick={onCancel}
    >
      <div
        data-testid="invitee-lookup-dialog-panel"
        className={`${modalStyles.modalContent} ${chromeStyles.modalContentSaveChanges}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={chromeStyles.modalSaveChangesHeader}>
          <h3
            data-testid="invitee-lookup-dialog-title"
          >
            We've found a matching profile
          </h3>
          <button
            type="button"
            data-testid="invitee-lookup-dialog-close-button"
            onClick={onCancel}
            className={modalStyles.closeButton}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className={chromeStyles.modalSaveChangesBody}>
          <div
            data-testid="invitee-lookup-dialog-details"
            className="space-y-3 text-center"
          >
            <p>
              <strong>Name:</strong> {lookupResult.first_name || 'N/A'} {lookupResult.last_name || 'N/A'}
            </p>
            {showDOB && lookupResult.birthdate && (
              <p>
                <strong>DOB:</strong> {formatDateForDisplay(lookupResult.birthdate).replace(/\//g, '-')}
              </p>
            )}
            {showSSN && (lookupResult.ssn || lookupResult.security_number) && (
              <p>
                <strong>SSN:</strong> {formatSSNForDisplay(lookupResult.ssn || lookupResult.security_number || '')}
              </p>
            )}
            <p>
              <strong>Mobile No:</strong> {lookupResult.mobile_no ? formatPhoneNumber(lookupResult.mobile_no) : 'N/A'}
            </p>
            <p>
              <strong>Email:</strong> {lookupResult.email || 'N/A'}
            </p>
          </div>
          <p
            data-testid="invitee-lookup-dialog-confirm-prompt"
            className="mt-5"
          >
            Please confirm that this information is correct
          </p>
        </div>
        <div className={chromeStyles.modalSaveChangesFooter}>
          <button
            type="button"
            data-testid="invitee-lookup-dialog-yes-button"
            onClick={onConfirm}
            className={chromeStyles.btnSaveChangesPrimary}
          >
            Yes
          </button>
          <button
            type="button"
            data-testid="invitee-lookup-dialog-no-button"
            onClick={onCancel}
            className={chromeStyles.btnSaveChangesSecondary}
          >
            No
          </button>
        </div>
      </div>
    </div>
  )
}
